// Landing page copy + data. Kept separate from the view so sections can be
// rearranged and the copy edited without touching component logic.

export interface RotatingHeadline {
  /** plain-text version, used for aria-label */
  plain: string
  /**
   * Static, author-controlled markup for the slot-machine reel: forces the
   * two-line break with <br> and marks the accent segment with
   * <span class="accent">.
   */
  html: string
}

/** Shared header block for a landing section (headline/lede are static, author-controlled markup). */
export interface SectionMeta {
  numeral: string
  eyebrow: string
  headlineHtml?: string
  ledeHtml?: string
}

export interface TransformItem {
  icon: string
  text: string
}

export interface Connector {
  name: string
  desc: string
  tag: string
  /** logo path under public/ (absolute), or omit for the bring-your-own tile */
  logo?: string
  byo?: boolean
}

export interface Provider {
  name: string
  logo?: string
  icon?: string
}

export interface FeatureCell {
  headline: string
  text: string
}

/** A single rich-text chat message. `html` is static, author-controlled copy. */
export interface ChatMessage {
  kind: 'message'
  role: 'user' | 'bot'
  label: string
  html: string
}

/** A block of tool calls the config agent made between messages. */
export interface ChatToolActivity {
  kind: 'tools'
  /** short mono caption shown on the block header (echoes the feature copy) */
  note: string
  calls: TraceToolCall[]
}

export type ChatEntry = ChatMessage | ChatToolActivity

export interface TraceToolCall {
  name: string
  args: string
  ret: string
  /** pretty-printed request payload; when present the call row expands to show it */
  json?: string
}

export interface TraceAgent {
  name: string
  tokens: string
  toolCalls?: TraceToolCall[]
  reportHeadline?: string
  reportText?: string
}

export interface Door {
  id: string
  tag: string
  tagIcon: string
  headline: string
  desc: string
  action: string
  fineprint?: string
}

/** Hero — rotating headlines (design landing.js `T`). */
export const rotatingHeadlines: RotatingHeadline[] = [
  { plain: 'Stop prompting. Start delegating.', html: 'Stop prompting.<br><span class="accent">Start delegating.</span>' },
  {
    plain: 'From plain-English request to production squad.',
    html: 'From plain-English request<br><span class="accent">to production squad.</span>'
  },
  {
    plain: 'Delegate the work you keep re-explaining.',
    html: 'Delegate the work<br><span class="accent">you keep re-explaining.</span>'
  },
  {
    plain: 'Your Tuesday, handled before you sit down.',
    html: '<span class="accent">Your Tuesday,</span> handled<br>before you sit down.'
  },
  { plain: 'Run a squad, not a chatbot.', html: 'Run a squad,<br><span class="accent">not a chatbot.</span>' }
]

export const heroBadge = 'MCP-native · Model-agnostic · Runs on your stack'
export const heroSub =
  "Most AI tools are strangers. Mission Squad is the agent that knows your stack — your Asana boards, your Drive folders, your Gmail, your Notion, your private data — and does the work you'd otherwise be doing at midnight."

/** Per-section header copy (numeral chip, eyebrow, headline with accent markup, lede). */
export const sections: Record<string, SectionMeta> = {
  transform: {
    numeral: 'I',
    eyebrow: 'The transformation',
    headlineHtml: 'Become the operator <span class="accent">your team can\'t replace.</span>',
    ledeHtml:
      "The work that buried your Tuesday now runs on a schedule, with a full trace, while you do the work only you can do. This isn't a chatbot. It's a way of operating."
  },
  configure: {
    numeral: 'II',
    eyebrow: 'The configuration agent',
    headlineHtml: 'Describe the squad. <span class="accent">It builds it.</span>',
    ledeHtml:
      "You don't assemble agents by hand. You tell the configuration agent what you want, and it builds the whole thing for you."
  },
  tools: {
    numeral: 'III',
    eyebrow: 'The tool chest',
    headlineHtml: 'It already speaks to <span class="accent">the tools you live in.</span>',
    ledeHtml:
      "Connect a server, authorize, and your squad can read and act in the systems you already use — Google Workspace, Gmail, Notion, Asana, Dropbox, Shopify, and dozens more. Need something we don't have yet? <span class=\"accent\">Drop in your own OAuth compatible MCP server</span> and it shows up alongside the rest. <a class=\"doc-link\" href=\"https://docs.missionsquad.ai/platform/mcp\" target=\"_blank\" rel=\"noopener noreferrer\">Read the MCP guide →</a>"
  },
  providers: {
    numeral: 'IV',
    eyebrow: 'The providers',
    headlineHtml: 'Bring your own brains. <span class="accent">Run it on your own metal.</span>',
    ledeHtml:
      "Mission Squad isn't married to one model vendor. Point an agent at OpenAI, Anthropic, Google, Cohere, Grok, Mistral — or run open models on hardware you own. Swap the model behind any agent in seconds, with no rewrite and no SDK to rip out."
  },
  patterns: {
    numeral: 'V',
    eyebrow: 'Two patterns',
    headlineHtml: 'Fan out, <span class="accent">or chain it.</span>',
    ledeHtml:
      'Mission Squad gives you two ways to compose agents. A <em>workflow</em> fans out across agents working in parallel; a <em>factory</em> chains steps in sequence. Many systems use both. <a class="doc-link" href="https://docs.missionsquad.ai/platform/workflows" target="_blank" rel="noopener noreferrer">Workflows →</a> · <a class="doc-link" href="https://docs.missionsquad.ai/platform/factories" target="_blank" rel="noopener noreferrer">Factories →</a>'
  },
  specimen: {
    numeral: 'VI',
    eyebrow: 'Specimen #1 · Anatomy of a real run',
    headlineHtml: 'Watch the work, <span class="accent">not a status bar.</span>',
    ledeHtml:
      "Most platforms show you a polished demo. We'll show you a real run — every agent that fired, every tool it called, the exact arguments it passed, and the data that came back."
  },
  promise: {
    numeral: 'VII',
    eyebrow: 'What we owe you'
  },
  doors: {
    numeral: 'VIII',
    eyebrow: 'Two doors',
    headlineHtml: 'Pick the one that fits <span class="accent">where you are</span> today.'
  }
}

/** Section I — the transformation. */
export const transformDo: TransformItem[] = [
  { icon: 'mdi-arrow-right', text: 'Assigning real work to a squad of agents that know your data and your tools' },
  { icon: 'mdi-arrow-right', text: 'Watching workflows run on a schedule with full traces — not hoping they worked' },
  { icon: 'mdi-arrow-right', text: 'Editing prompts and swapping models in seconds, with no rewrites' },
  { icon: 'mdi-arrow-right', text: 'Deploying inside your own VPC or on-prem, with keys and secrets you control' }
]

export const transformDont: TransformItem[] = [
  { icon: 'mdi-close', text: 'Vendor lock-in dressed up as "platform" with hostage SDKs' },
  { icon: 'mdi-close', text: "Hallucinations from agents that can't read your private data" },
  { icon: 'mdi-close', text: 'Glue code stitching brittle scripts to half-working integrations' },
  { icon: 'mdi-close', text: 'Privacy theater where "your data" means "their training set"' }
]

/** Section II — the configuration agent. */
export const configChat: { agent: string; thread: string; sub: string; entries: ChatEntry[] } = {
  agent: 'msq-config-agent',
  thread: 'YouTube Agent Research Factory',
  sub: 'MissionSquad Configuration Assistant',
  entries: [
    {
      kind: 'message',
      role: 'user',
      label: 'You',
      html:
        "create a factory consisting of 2 agents, 1 that will search youtube for phrases similar to the user's input. like the user will give a word or phrase and the agent should perform a few searches for better results, and then the 1st agent will respond with youtube links and video titles. the second agent, who will receive the 1st agent's results, should get the transcripts for the 5 most relevant videos, create summaries of each and then combine the summaries for a holistic summary of the topic or search."
    },
    {
      kind: 'tools',
      note: 'reads your stack first',
      calls: [
        {
          name: 'msq_list_servers',
          args: '{}',
          ret: '[{"name":"youtube-transcripts","displayName":"YouTube Transcripts","transportType":"stdio","description":"Search YouTube and fetch video transcripts…'
        },
        {
          name: 'msq_list_server_tools',
          args: '{"serverName":"youtube-transcripts"}',
          ret: '{"tools":[{"name":"search_youtube","description":"Search YouTube for videos matching a query…"},{"name":"get_youtube_transcript"…'
        }
      ]
    },
    {
      kind: 'message',
      role: 'bot',
      label: 'msq-config-agent',
      html: `<p>I have analyzed your request and designed a sequential multi-step <strong>Factory</strong> to accomplish this. Here is the proposed plan:</p>
<p class="plan-h">1. Proposed Agents</p>
<div class="plan-agent">
  <p class="plan-agent-name">Agent 1: YouTube Searcher (<code>youtube-searcher</code>)</p>
  <ul>
    <li><strong>Description:</strong> Searches YouTube for phrases similar to the user's input, performs multiple searches to find the best results, and returns video titles and links.</li>
    <li><strong>Model:</strong> <code>claude-sonnet-4-6:1m</code></li>
    <li><strong>Tools:</strong> <code>search_youtube</code> (from the <code>youtube-transcripts</code> MCP server)</li>
  </ul>
</div>
<div class="plan-agent">
  <p class="plan-agent-name">Agent 2: YouTube Transcript Summarizer (<code>youtube-transcript-summarizer</code>)</p>
  <ul>
    <li><strong>Description:</strong> Receives a list of YouTube video links, fetches transcripts for the 5 most relevant videos, summarizes each, and combines them into a holistic summary.</li>
    <li><strong>Model:</strong> <code>claude-sonnet-4-6:1m</code></li>
    <li><strong>Tools:</strong> <code>get_youtube_transcript</code> (from the <code>youtube-transcripts</code> MCP server)</li>
  </ul>
</div>
<p class="plan-h">2. Proposed Factory Structure</p>
<ul>
  <li><strong>Factory Name:</strong> YouTube Topic Research Factory</li>
  <li><strong>Description:</strong> Sequential research pipeline that searches YouTube for a topic, retrieves transcripts for the top 5 videos, and synthesizes a holistic summary.</li>
  <li><strong>Steps:</strong> <code>youtube-searcher</code> → <code>youtube-transcript-summarizer</code></li>
</ul>
<p>Please let me know if you approve of this plan, and I will proceed with generating the prompts, creating the agents, and configuring the factory!</p>`
    },
    { kind: 'message', role: 'user', label: 'You', html: 'great. create this now.' },
    {
      kind: 'tools',
      note: 'writes prompts · creates agents · wires the factory',
      calls: [
        {
          name: 'msq_generate_prompt',
          args: '{"type":"agent","name":"youtube-searcher","model":"claude-sonnet-4-6:1m"…}',
          ret: '{"promptId":"…","prompt":"You are the YouTube Searcher, a research agent. Given a word or phrase, run several…'
        },
        {
          name: 'msq_generate_prompt',
          args: '{"type":"agent","name":"youtube-transcript-summarizer","model":"claude-sonnet-4-6:1m"…}',
          ret: '{"promptId":"…","prompt":"You are the YouTube Transcript Summarizer. You receive a list of videos, fetch the…'
        },
        {
          name: 'msq_add_agent',
          args: '{"name":"youtube-searcher","model":"claude-sonnet-4-6:1m","systemPromptId":"…","tools":["search_youtube"]}',
          ret: '{"data":{"id":"…","name":"youtube-searcher"},"publish":{…'
        },
        {
          name: 'msq_add_agent',
          args: '{"name":"youtube-transcript-summarizer","model":"claude-sonnet-4-6:1m","systemPromptId":"…","tools":["get_youtube_transcript"]}',
          ret: '{"data":{"id":"…","name":"youtube-transcript-summarizer"},"publish":{…'
        },
        {
          name: 'msq_create_factory',
          args: '{"name":"YouTube Topic Research Factory","steps":[{"kind":"agent","agentRef":"agent/…"},{"kind":"agent","agentRef":"agent/…"}]}',
          ret: '{"id":"…","name":"YouTube Topic Research Factory","steps":[…'
        }
      ]
    },
    {
      kind: 'message',
      role: 'bot',
      label: 'msq-config-agent',
      html: `<p>I have successfully created and configured your <strong>YouTube Topic Research Factory</strong>. Here is what was set up in your MissionSquad account:</p>
<ul class="plan-created">
  <li><i class="mdi mdi-check-circle-outline"></i> Agent created — <code>youtube-searcher</code></li>
  <li><i class="mdi mdi-check-circle-outline"></i> Agent created — <code>youtube-transcript-summarizer</code></li>
  <li><i class="mdi mdi-check-circle-outline"></i> Factory created — YouTube Topic Research Factory</li>
</ul>`
    }
  ]
}

/** Outcome strip under the config chat (mirrors the trace plate's run footer). */
export const configOutcome = {
  headline: 'Factory deployed',
  detail: 'youtube-searcher → youtube-transcript-summarizer',
  meta: '2 agents · 1 factory · ready to run'
}

// Prose paragraphs (static, trusted copy; may contain <em>/<code>/accent markup).
export const configProse: string[] = [
  'Ask for <em>"a factory that searches YouTube for a topic, pulls transcripts for the top five videos, and writes one holistic summary,"</em> and it doesn\'t just start typing. It checks what you already have, then proposes a plan: each agent, the model it runs on, the tools it needs, and how they hand off to each other. You approve the plan. It writes the prompts, creates the agents, and wires up the factory.',
  'Changed your mind? Tell it — <em>"use GPT 5.4 mini instead of Sonnet"</em> — and it reconfigures in place. <span class="accent">No rebuild. No config files. No clicking through twelve forms.</span> <a class="doc-link" href="https://docs.missionsquad.ai/platform/getting-started" target="_blank" rel="noopener noreferrer">Get started in the docs →</a>'
]

export const providersProse: string[] = [
  'And it runs where you do. Deploy in your own VPC or on-prem so your keys, your data, and your traffic never leave your control. <span class="accent">"Your data" means your data</span> — not a polite word for someone\'s training set. <a class="doc-link" href="https://docs.missionsquad.ai/hosting/" target="_blank" rel="noopener noreferrer">See the self-hosting guide →</a>'
]

export const specimenProse: string[] = [
  'Here\'s a financial-analysis workflow: a single request fans out to nine specialist agents — fundamentals, sentiment, macro, and six technical analysts — each pulling its own data and writing its own section, before a lead agent stitches them into one report. Open any sub-agent and you can read the tool calls underneath: <code>fundamentalData_companyOverview</code>, the income statement, the balance sheet, the cash flow — and the JSON each one returned.',
  'Every step is counted. <span class="accent">When a run does something you didn\'t expect, you don\'t guess — you open the trace and read exactly what happened.</span>'
]

export const configCaption = 'Real config session — request → proposed plan → created agents + factory'

export const configFeatures: FeatureCell[] = [
  {
    headline: 'It reads your stack first.',
    text: 'Before it proposes anything, it lists your connected MCP servers and the tools they expose — so the plan fits what you have.'
  },
  {
    headline: 'It proposes before it builds.',
    text: 'You see the agents, models, and hand-offs as a plan you approve — not a black box that silently spins up infrastructure.'
  },
  {
    headline: 'It edits by conversation.',
    text: 'Swap a model, rewire a step, rename an agent. The squad changes as fast as you can describe the change.'
  }
]

/** Section III — connectors (logos under public/img/mcp-servers). */
export const connectors: Connector[] = [
  { name: 'Google Workspace', desc: 'Docs, Drive, Gmail, and Calendar.', tag: 'Productivity', logo: '/img/mcp-servers/google-workspace.png' },
  { name: 'Notion', desc: 'Pages, databases, and blocks.', tag: 'Docs', logo: '/img/mcp-servers/notion.png' },
  { name: 'Asana', desc: 'Projects and tasks.', tag: 'Project mgmt', logo: '/img/mcp-servers/asana.png' },
  { name: 'Dropbox', desc: 'Files and sharing.', tag: 'Storage', logo: '/img/mcp-servers/dropbox.png' },
  { name: 'Cloudflare', desc: 'Workers, DNS, and APIs.', tag: 'Infra', logo: '/img/mcp-servers/cloudflare-api.png' },
  { name: 'Shopify', desc: 'Stores, products, and orders.', tag: 'Commerce', logo: '/img/mcp-servers/shopify.png' },
  { name: 'Monday.com', desc: 'Boards and items.', tag: 'Project mgmt', logo: '/img/mcp-servers/monday.png' },
  { name: 'Calendly', desc: 'Events and scheduling.', tag: 'Scheduling', logo: '/img/mcp-servers/calendly.png' },
  { name: 'Klaviyo', desc: 'Email and SMS campaigns.', tag: 'Marketing', logo: '/img/mcp-servers/klaviyo.png' },
  { name: 'Amplitude', desc: 'Product analytics and cohorts.', tag: 'Analytics', logo: '/img/mcp-servers/amplitude.png' },
  { name: 'Supabase', desc: 'Postgres, auth, and storage.', tag: 'Database', logo: '/img/mcp-servers/supabase.png' },
  { name: 'Make.com', desc: 'Multi-step automation scenarios.', tag: 'Automation', logo: '/img/mcp-servers/make-com.png' },
  { name: 'CoinGecko', desc: 'Crypto prices and markets.', tag: 'Markets', logo: '/img/mcp-servers/coingecko.png' },
  { name: 'Beehiiv', desc: 'Newsletters and subscribers.', tag: 'Newsletter', logo: '/img/mcp-servers/beehiiv.png' },
  { name: 'Cloudinary', desc: 'Media storage and transforms.', tag: 'Media', logo: '/img/mcp-servers/cloudinary.png' },
  { name: 'DeepWiki', desc: 'Repository docs and Q&A.', tag: 'Docs', logo: '/img/mcp-servers/deepwiki.png' },
  { name: 'Context7', desc: 'Up-to-date library docs.', tag: 'Docs', logo: '/img/mcp-servers/context7.png' },
  { name: 'Neon', desc: 'Serverless Postgres.', tag: 'Database', logo: '/img/mcp-servers/neon.png' },
  { name: 'Bring your own MCP server', desc: 'Drop in any MCP server and it shows up alongside the rest.', tag: 'Drop-in', byo: true }
]

export const connectorsCaption = 'All connections · OAuth 2.1 · DCR · PKCE · audited permission scopes'

/** Section IV — providers (logos under public/img/ai-provider-icons). */
export const providers: Provider[] = [
  { name: 'OpenAI', logo: '/img/ai-provider-icons/openai/png/light/openai.png' },
  { name: 'Anthropic', logo: '/img/ai-provider-icons/anthropic/png/light/anthropic.png' },
  { name: 'Google Gemini', logo: '/img/ai-provider-icons/google-gemini/png/light/gemini-color.png' },
  { name: 'Grok', logo: '/img/ai-provider-icons/grok/png/light/grok.png' },
  { name: 'Groq', logo: '/img/ai-provider-icons/groq/png/light/groq.png' },
  { name: 'Mistral', logo: '/img/ai-provider-icons/mistral/png/light/mistral-color.png' },
  { name: 'Cohere', logo: '/img/ai-provider-icons/cohere/png/light/cohere-color.png' },
  { name: 'Microsoft Copilot', logo: '/img/ai-provider-icons/microsoft-copilot/png/light/copilot-color.png' },
  { name: 'OpenRouter', logo: '/img/ai-provider-icons/openrouter/png/light/openrouter.png' },
  { name: 'ElevenLabs', logo: '/img/ai-provider-icons/elevenlabs/png/light/elevenlabs.png' },
  { name: 'Self-hosted & open models', icon: 'mdi-server-network' }
]

/** Section VI — specimen trace. */
export const traceTitle = 'Short-term financial analysis'
export const traceStamp = 'Plate #1'
export const traceRunMeta = '908517 total tokens · completed 5/28/2026'

export const traceAgents: TraceAgent[] = [
  {
    name: 'fundamental-analyst',
    tokens: '97443 prompt · 5137 completion · 102580 total',
    toolCalls: [
      { name: 'fundamentalData_companyOverview', args: '{"symbol":"AAPL"}', ret: '{"symbol":"AAPL","assetType":"Common Stock","name":"Apple Inc","description":"Apple Inc. is an America…' },
      { name: 'fundamentalData_incomeStatement', args: '{"symbol":"AAPL"}', ret: '{"symbol":"AAPL","annualReports":[{"fiscalDateEnding":"2025-09-30","reportedCurrency":"USD","grossProfit"…' },
      { name: 'fundamentalData_balanceSheet', args: '{"symbol":"AAPL"}', ret: '{"symbol":"AAPL","annualReports":[{"fiscalDateEnding":"2025-09-30","reportedCurrency":"USD","totalAssets":"35…' },
      { name: 'fundamentalData_cashFlow', args: '{"symbol":"AAPL"}', ret: '{"symbol":"AAPL","annualReports":[{"fiscalDateEnding":"2025-09-30","reportedCurrency":"USD","operatingCashflow"…' },
      {
        name: 'fundamentalData_earnings',
        args: '{"symbol":"AAPL"}',
        ret: '{"symbol":"AAPL","annualEarnings":[{"fiscalDateEnding":"2026-03-31","reportedEPS":4.85},{"fiscalDateEnding":"2025…',
        json: '{\n  "toolName": "fundamentalData_earnings",\n  "serverName": "alphavantage",\n  "args": { "symbol": "AAPL" }\n}'
      }
    ],
    reportHeadline: 'Apple Inc. (AAPL) — Fundamental Analysis Report',
    reportText:
      'Apple is a large-cap, highly profitable integrated hardware/software/services company with a dominant premium device position and expanding recurring-services revenue. Fiscal 2025 results show revenue of $416.2B and net income of $112.0B…'
  },
  { name: 'sentiment-analyst', tokens: '20805 prompt · 2802 completion · 23607 total' },
  { name: 'macro-analyst', tokens: '2724 prompt · 1932 completion · 4656 total' },
  { name: 'technical-analyst-daily', tokens: '4402 prompt · 1673 completion · 6075 total' },
  { name: 'technical-analyst-sma', tokens: '106558 prompt · 1009 completion · 107567 total' },
  { name: 'technical-analyst-ema', tokens: '99886 prompt · 934 completion · 100820 total' },
  { name: 'technical-analyst-momentum', tokens: '218880 prompt · 957 completion · 219837 total' },
  { name: 'technical-analyst-bbands', tokens: '231722 prompt · 950 completion · 232672 total' },
  { name: 'technical-analyst-atr', tokens: '99770 prompt · 890 completion · 100660 total' }
]

export const traceMainTokens = '7339 prompt · 1530 completion · 10043 total'
export const traceMainText =
  'Apple Inc. (AAPL) presents as a high-quality, market-leading technology firm demonstrating exceptional financial performance and strong short-term market momentum…'
export const traceDisclaimer = 'Model-generated analysis, not investment advice.'
export const traceFoot =
  'Plate #1 · Short-term financial analysis — 9 sub-agents · full tool-call trace · per-step token accounting · kept in run history alongside every prior run.'

/** Section VII — the promise. */
export const promise = {
  statementHtml:
    'Your agents will run <span class="accent">when you need them.</span> And when they don\'t, <span class="accent">you will know why.</span>',
  meta: [
    { label: 'What we keep', value: 'Reliability · Observability · Your data, your keys' },
    { label: "What we don't claim", value: 'Magic · The cheapest · Infallibility' },
    { label: 'How to test it', value: 'Run a workflow. Read the trace. Decide.' }
  ]
}

/** Section VIII — two doors. */
export const doors: Door[] = [
  {
    id: 'briefing',
    tag: 'Door #1 · The briefing',
    tagIcon: 'mdi-email-newsletter',
    headline: 'Get the field briefing.',
    desc: "A weekly note from the Mission Squad lab: workflows we built, what broke, what we learned, what we'd do differently. No pitch. No sales follow-up. Unsubscribe in one click.",
    action: 'Subscribe to the briefing'
  },
  {
    id: 'build',
    tag: 'Door #2 · The build',
    tagIcon: 'mdi-rocket-launch-outline',
    headline: 'Deploy your first squad.',
    desc: "Free to start. No credit card. Connect one tool, describe one agent, and read the trace yourself. If you're not up and running in an afternoon, we got it wrong and want to hear about it.",
    action: 'Start building',
    fineprint: 'Cloud or on-prem · OAuth 2.1 · MCP-native'
  }
]
